<script setup lang="ts">
import { t } from "../i18n";
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import EmojiPicker from "./EmojiPicker.vue";
import ReactionBar from "./ReactionBar.vue";
import { useEscapeLayer } from "../composables/escape";
const props = defineProps<{
  open: boolean;
  x: number;
  y: number;
  selected?: string;
  /** Opens on the whole catalog, as the menu's + does. */
  startExpanded?: boolean;
}>();
const emit = defineEmits<{ choose: [emoji: string]; close: [] }>();
const root = ref<HTMLElement>(),
  expanded = ref(false);
const position = ref({
  left: "0px",
  top: "0px",
  visibility: "hidden" as "hidden" | "visible",
});
let previous: HTMLElement | null = null;
function close() {
  emit("close");
}
function choose(emoji: string) {
  emit("choose", emoji);
  close();
}
function place() {
  if (!root.value) return;
  const rect = root.value.getBoundingClientRect();
  position.value = {
    left: Math.max(8, Math.min(props.x, innerWidth - rect.width - 8)) + "px",
    top:
      Math.max(
        8,
        Math.min(props.y - rect.height - 10, innerHeight - rect.height - 8),
      ) + "px",
    visibility: "visible",
  };
}
async function collapse() {
  expanded.value = false;
  await nextTick();
  root.value
    ?.querySelector<HTMLButtonElement>(".reaction-more")
    ?.focus({ preventScroll: true });
}
useEscapeLayer(() => props.open, close);
useEscapeLayer(() => props.open && expanded.value, collapse);
watch(
  () => props.open,
  async (open) => {
    expanded.value = open && props.startExpanded === true;
    if (open) {
      previous = document.activeElement as HTMLElement;
      position.value.visibility = "hidden";
      await nextTick();
      if (!props.open) return;
      place();
      // Hidden until placed, and a hidden button cannot take the focus.
      await nextTick();
      if (!props.open) return;
      // Opened on the catalog, its search takes the focus (it was hidden when it mounted).
      if (expanded.value)
        root.value
          ?.querySelector<HTMLInputElement>(".reaction-catalog input")
          ?.focus({ preventScroll: true });
      else
        (
          root.value?.querySelector<HTMLButtonElement>(
            '.reaction-picker button[aria-pressed="true"]',
          ) ||
          root.value?.querySelector<HTMLButtonElement>(
            ".reaction-picker button",
          )
        )?.focus({ preventScroll: true });
      window.addEventListener("resize", place);
    } else {
      window.removeEventListener("resize", place);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    }
  },
  { immediate: true, flush: "post" },
);
watch([expanded, () => props.x, () => props.y], async () => {
  await nextTick();
  if (props.open) place();
});
onBeforeUnmount(() => window.removeEventListener("resize", place));
function key(event: KeyboardEvent) {
  const buttons = [
    ...(root.value?.querySelectorAll<HTMLElement>(
      "button:not(:disabled), input",
    ) || []),
  ];
  const current = document.activeElement as HTMLElement;
  const index = buttons.indexOf(current);
  if (event.key === "Tab") {
    if (
      (event.shiftKey && index <= 0) ||
      (!event.shiftKey && index === buttons.length - 1)
    ) {
      event.preventDefault();
      buttons[event.shiftKey ? buttons.length - 1 : 0]?.focus();
    }
  } else if (
    current.closest(".reaction-picker") &&
    ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
  ) {
    const quickButtons = [
      ...root.value!.querySelectorAll<HTMLButtonElement>(
        ".reaction-picker button",
      ),
    ];
    const i = quickButtons.indexOf(current as HTMLButtonElement);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? quickButtons.length - 1
          : (i + (event.key === "ArrowRight" ? 1 : -1) + quickButtons.length) %
            quickButtons.length;
    event.preventDefault();
    quickButtons[next]?.focus();
  }
}
</script>
<template>
  <Teleport to="body">
    <template v-if="open">
      <div class="reaction-veil" @click="close" />
      <div
        ref="root"
        class="reaction-popover"
        :style="position"
        role="dialog"
        :aria-label="t('message.react')"
        @keydown="key"
      >
        <EmojiPicker
          v-if="expanded"
          class="reaction-catalog"
          reactions-only
          autofocus
          :selected="selected"
          @choose="choose"
          @close="collapse"
        />
        <ReactionBar
          :selected="selected"
          :expanded="expanded"
          @choose="choose"
          @more="expanded ? collapse() : (expanded = true)"
        />
      </div>
    </template>
  </Teleport>
</template>
